import type {
  BiomapHabitatClassification,
  BiomapMsaComponent,
  IBiomapRasterIdentity,
  IBiomapRasterLayerDescriptor,
} from '../../interfaces/biomap/IBiomapLayer';
import type { IRasterLayer } from '../../interfaces/sjmap2/raster/IRaster';

const normalize = (value?: string | null): string =>
  (value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');

const includesAny = (value: string, candidates: readonly string[]): boolean =>
  candidates.some((candidate) => value.includes(candidate));

const HABITAT_COLLECTION_MARKERS = [
  'biomap habitat mapping',
  'habitat mapping',
  'habitat',
] as const;

const MSA_COLLECTION_MARKERS = [
  'biomap terrestrial msa',
  'terrestrial msa',
  'msa',
] as const;

const resolveHabitatClassification = (
  searchableText: string,
): BiomapHabitatClassification | null => {
  if (includesAny(searchableText, ['iucn l2', 'level 2', ' l2'])) {
    return 'l2';
  }

  if (includesAny(searchableText, ['iucn l1', 'level 1', ' l1'])) {
    return 'l1';
  }

  return null;
};

const resolveMsaComponent = (searchableText: string): BiomapMsaComponent => {
  if (includesAny(searchableText, ['land use', 'landuse'])) {
    return 'land_use';
  }

  if (includesAny(searchableText, ['climate change', 'climate'])) {
    return 'climate';
  }

  if (includesAny(searchableText, ['nitrogen deposition', 'nitrogen'])) {
    return 'nitrogen';
  }

  if (
    includesAny(searchableText, [
      'infrastructure disturbance',
      'infra disturbance',
      'infrastructure',
    ])
  ) {
    return 'infra_disturbance';
  }

  if (searchableText.includes('fragmentation')) {
    return 'fragmentation';
  }

  return 'overall';
};

/**
 * Maps an Explorer raster to the BIOMAP semantic identity used by the UI.
 *
 * BIOMAP intentionally keeps its internal layer_key in artifact provenance rather
 * than RasterLayer.properties, so Explorer receives the stable publication name /
 * collection_name pair. Keep the matching rules centralized here instead of
 * scattering string checks through components.
 */
export const getBiomapRasterIdentity = (
  layer: IRasterLayer,
): IBiomapRasterIdentity | null => {
  const collectionName = normalize(layer.collection_name);
  const name = normalize(layer.name);
  const description = normalize(layer.description);
  const searchableText = [collectionName, name, description]
    .filter(Boolean)
    .join(' ');

  const isHabitat = includesAny(collectionName, HABITAT_COLLECTION_MARKERS)
    || includesAny(name, ['habitat mapping', 'habitat iucn']);

  if (isHabitat) {
    const habitatClassification = resolveHabitatClassification(searchableText);
    return habitatClassification
      ? { product: 'habitat', habitatClassification }
      : null;
  }

  const isMsa = includesAny(collectionName, MSA_COLLECTION_MARKERS)
    || includesAny(name, ['terrestrial msa', ' msa']);

  if (isMsa) {
    return {
      product: 'msa',
      msaComponent: resolveMsaComponent(searchableText),
    };
  }

  return null;
};

export const getBiomapRasterLayerDescriptor = (
  layer: IRasterLayer,
): IBiomapRasterLayerDescriptor | null => {
  const identity = getBiomapRasterIdentity(layer);
  return identity ? { layer, identity } : null;
};
